import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Elements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import { useOrder } from '../context/OrderContext';
import { AddressForm } from '../components/AddressForm';
import { OrderSummary } from '../components/OrderSummary';
import { ConsentCheckbox } from '../components/ConsentCheckbox';
import { PaymentForm } from '../components/PaymentForm';
import { api, QuoteResponse } from '../lib/api';

const stripePromise = loadStripe(import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY);

export function CheckoutPage() {
  const { items, shippingAddress, customer, setShippingAddress, setCustomer, setQuote } = useOrder();
  const navigate = useNavigate();

  const [email, setEmail] = useState('');
  const [consent, setConsent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [addressErrors, setAddressErrors] = useState<Record<string, string>>({});
  const [quote, setQuoteLocal] = useState<QuoteResponse | null>(null);
  const [orderResult, setOrderResultLocal] = useState<{
    orderId: string;
    orderReferenceId: string;
    clientSecret: string | null;
  } | null>(null);

  const defaultAddress = {
    firstName: '',
    lastName: '',
    addressLine1: '',
    addressLine2: '',
    city: '',
    postalCode: '',
    country: 'DE',
  };

  useEffect(() => {
    if (!shippingAddress) setShippingAddress(defaultAddress);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shippingAddress]);

  const validateAddress = (addr: any) => {
    const errors: Record<string, string> = {};
    if (!addr.firstName) errors.firstName = 'Vorname erforderlich';
    if (!addr.lastName) errors.lastName = 'Nachname erforderlich';
    if (!addr.addressLine1) errors.addressLine1 = 'Straße erforderlich';
    if (!addr.city) errors.city = 'Stadt erforderlich';
    if (!addr.postalCode) errors.postalCode = 'PLZ erforderlich';
    if (!addr.country) errors.country = 'Land erforderlich';
    setAddressErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleQuote = async (): Promise<QuoteResponse | null> => {
    if (!validateAddress(shippingAddress!)) return null;
    if (!email || !consent) {
      setError('E-Mail und Einwilligung erforderlich');
      return null;
    }
    setLoading(true);
    setError(null);
    try {
      const q = await api.getQuote({
        items: items.map((i) => ({ productUid: i.productUid, quantity: i.quantity, fileUrl: i.fileUrl })),
        shippingAddress: shippingAddress!,
      });
      setQuote(q);
      setQuoteLocal(q);
      return q;
    } catch (e: any) {
      setError(e.message);
      return null;
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (!validateAddress(shippingAddress!)) return;
    if (!email || !consent) {
      setError('E-Mail und Einwilligung erforderlich');
      return;
    }
    if (!customer) {
      setCustomer({ email, firstName: shippingAddress!.firstName, lastName: shippingAddress!.lastName });
    }

    setLoading(true);
    setError(null);
    try {
      const order = await api.createOrder({
        customer: { email, firstName: shippingAddress!.firstName, lastName: shippingAddress!.lastName },
        shippingAddress: shippingAddress!,
        items,
      });
      setOrderResultLocal({
        orderId: order.orderId,
        orderReferenceId: order.orderReferenceId,
        clientSecret: order.clientSecret ?? null,
      });
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  if (orderResult?.clientSecret) {
    return (
      <div>
        <h2>Zahlung</h2>
        <div className="card" style={{ maxWidth: 480, margin: '0 auto' }}>
          <p>
            <strong>Bestellreferenz:</strong> {orderResult.orderReferenceId}
          </p>
          <Elements stripe={stripePromise} options={{ clientSecret: orderResult.clientSecret }}>
            <PaymentForm
              onSuccess={() => navigate(`/order/${orderResult.orderId}`)}
              returnUrl={`${window.location.origin}/order/${orderResult.orderId}`}
            />
          </Elements>
        </div>
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <div style={{ textAlign: 'center', padding: '3rem' }}>
        <p>Keine Produkte im Warenkorb.</p>
        <a href="/design">
          <button className="btn btn-primary">Design wählen</button>
        </a>
      </div>
    );
  }

  return (
    <div>
      <h2>Schritt 2: Kasse</h2>
      <div className="grid grid-2" style={{ gap: '2rem' }}>
        <div>
          <h3>Lieferadresse</h3>
          <AddressForm
            value={shippingAddress || defaultAddress}
            onChange={setShippingAddress}
            errors={addressErrors}
          />

          <div className="form-group">
            <label>E-Mail *</label>
            <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required />
          </div>

          <ConsentCheckbox checked={consent} onChange={setConsent} />

          <button
            className="btn btn-secondary"
            onClick={handleQuote}
            disabled={loading}
            style={{ marginTop: '1rem', width: '100%' }}
          >
            {loading ? '⏳ Lade Quote…' : 'Versandkosten berechnen'}
          </button>

          {error && <p className="error" style={{ marginTop: '1rem' }}>{error}</p>}
        </div>

        <div>
          <OrderSummary items={items} quote={quote} />

          <button
            className="btn btn-primary"
            onClick={handleSubmit}
            disabled={loading}
            style={{ marginTop: '1rem', width: '100%' }}
          >
            {loading ? '⏳ Lege Bestellung an…' : 'Zur Zahlung'}
          </button>
        </div>
      </div>
    </div>
  );
}
